import moment from 'moment-timezone';
import {
  FUEL_EVENT_FILTER,
  eventLabelToFilterKey,
} from './fuelEventConstants';

const toNumber = (value) => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const pickNumeric = (...candidates) => {
  for (let i = 0; i < candidates.length; i += 1) {
    const numeric = toNumber(candidates[i]);
    if (numeric !== null) return numeric;
  }
  return null;
};

const toTimestamp = (value) => {
  const timestamp = moment(value).valueOf();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const normalizeTelemetrySourceKey = (sourceType) => {
  if (sourceType == null || sourceType === '') return null;
  if (typeof sourceType === 'string') return sourceType;
  if (typeof sourceType === 'object') {
    return sourceType.value ?? sourceType.name ?? sourceType.fuelSource ?? null;
  }
  return null;
};

const API_FUEL_SOURCES = new Set([
  'obd', 'can', 'fms', 'eld', 'externalSensor', 'dashboard', 'analog1', 'analog2', 'analog3',
]);

const API_FUEL_SOURCE_ALIASES = {
  externalsensor: 'externalSensor',
  external_sensor: 'externalSensor',
  sensor: 'externalSensor',
  analog1: 'analog1',
  analog2: 'analog2',
  analog3: 'analog3',
};

/** Map device fuelSource to GET /v3/getFuelLevelSignal enum (defaults to obd). */
export const normalizeApiFuelSourceKey = (sourceType) => {
  const rawKey = normalizeTelemetrySourceKey(sourceType);
  if (rawKey == null || rawKey === '') return 'obd';
  if (API_FUEL_SOURCES.has(rawKey)) return rawKey;

  const lower = String(rawKey).trim().toLowerCase();
  if (API_FUEL_SOURCE_ALIASES[lower]) return API_FUEL_SOURCE_ALIASES[lower];

  const lowerToCanonical = {
    obd: 'obd',
    can: 'can',
    fms: 'fms',
    eld: 'eld',
    dashboard: 'dashboard',
  };
  if (lowerToCanonical[lower]) return lowerToCanonical[lower];

  return 'obd';
};

const fuelLevelSignalResponseCache = new Map();
const FUEL_LEVEL_SIGNAL_CACHE_LIMIT = 250;

export const buildFuelLevelSignalCacheKey = (deviceOid, fromTime, toTime, fuelSource) => (
  `${deviceOid}|${fromTime}|${toTime}|${fuelSource}`
);

export const readFuelLevelSignalCache = (cacheKey) => (
  fuelLevelSignalResponseCache.get(cacheKey) ?? null
);

export const writeFuelLevelSignalCache = (cacheKey, payload) => {
  if (fuelLevelSignalResponseCache.size >= FUEL_LEVEL_SIGNAL_CACHE_LIMIT) {
    const oldestKey = fuelLevelSignalResponseCache.keys().next().value;
    fuelLevelSignalResponseCache.delete(oldestKey);
  }
  fuelLevelSignalResponseCache.set(cacheKey, payload);
};

const parseJsonIfString = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (err) {
    return value;
  }
};

const readFuelLitresFromBucket = (bucket, tankCapacityL) => {
  if (!bucket || typeof bucket !== 'object') return null;

  const litres = pickNumeric(
    bucket.fuelLevelLitre,
    bucket.fuel_level_litre,
    bucket.fuelLevelLitres,
    bucket.fuel_level_litres,
  );
  if (litres !== null) return litres;

  const percentage = pickNumeric(
    bucket.fuelLevelPerc,
    bucket.fuelLevelPercentage,
    bucket.fuel_level_percentage,
    bucket.fuelLevelPercent,
    bucket.fuel_level_percent,
  );
  if (percentage !== null && tankCapacityL > 0) {
    return (percentage / 100) * tankCapacityL;
  }

  return null;
};

/** Resolve fuel level (L) from flat or fuelSource-nested telemetry rows. */
export const resolveRowFuelLitres = (row = {}, fuelSource, tankCapacityL = 0) => {
  const topLevelLitres = pickNumeric(
    row.fuelLevelLitre,
    row.fuel_level_litre,
    row.fuelLevelLitres,
    row.fuel_level_litres,
  );
  if (topLevelLitres !== null) return topLevelLitres;

  const topLevelPct = pickNumeric(
    row.fuelLevelPerc,
    row.fuelLevelPercentage,
    row.fuel_level_percentage,
    row.fuel_level_percent,
  );
  if (topLevelPct !== null && tankCapacityL > 0) {
    return (topLevelPct / 100) * tankCapacityL;
  }

  const sourceKey = normalizeTelemetrySourceKey(fuelSource);
  const cachedData = row.cachedData || row.cached_data || {};

  if (sourceKey) {
    const fromSource = readFuelLitresFromBucket(row[sourceKey], tankCapacityL)
      ?? readFuelLitresFromBucket(cachedData[sourceKey], tankCapacityL);
    if (fromSource !== null) return fromSource;
  }

  const sourceBuckets = Object.values(cachedData);
  for (let i = 0; i < sourceBuckets.length; i += 1) {
    const fromBucket = readFuelLitresFromBucket(sourceBuckets[i], tankCapacityL);
    if (fromBucket !== null) return fromBucket;
  }

  return null;
};

const resolveRowTimestamp = (row = {}) => {
  const candidates = [
    row.timestamp,
    row.bqTimestamp,
    row.bq_timestamp,
    row.bqTimestampHour,
    row.bq_timestamp_hour,
    row.eventTime,
    row.event_time,
    row.sampleTime,
    row.sample_time,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate == null || candidate === '') continue;
    if (typeof candidate === 'object' && candidate.value != null) {
      return candidate.value;
    }
    return candidate;
  }

  return null;
};

const resolveRowLocation = (row = {}) => {
  const sources = [row].filter((source) => source && typeof source === 'object');

  for (let index = 0; index < sources.length; index += 1) {
    const source = sources[index];
    const latitude = pickNumeric(
      source.latitude,
      source.lat,
      source.eventLatitude,
      source.event_lat,
    );
    const longitude = pickNumeric(
      source.longitude,
      source.lng,
      source.lon,
      source.eventLongitude,
      source.event_lon,
    );
    if (latitude != null && longitude != null) {
      return { latitude, longitude };
    }
  }

  return { latitude: null, longitude: null };
};

const buildEventRecord = (sample, overrides = {}) => {
  const eventLabel = overrides.event || 'EVENT';
  return {
    x: overrides.x,
    xStart: overrides.xStart ?? overrides.x ?? null,
    time: sample.timestamp,
    timeStart: overrides.timeStart ?? sample.timestamp,
    event: eventLabel,
    eventFilterKey: overrides.eventFilterKey || eventLabelToFilterKey(eventLabel),
    deltaLitres: overrides.deltaLitres ?? null,
    levelAfter: overrides.levelAfter ?? null,
    baselineLevel: overrides.baselineLevel ?? null,
    fuelLevelLitre: overrides.fuelLevelLitre ?? sample.rawFuelL,
    latitude: sample.latitude,
    longitude: sample.longitude,
    estimatedCostRM: sample.estimatedCostRM,
    motionLabel: overrides.motionLabel,
    eventSub: overrides.eventSub,
    engineOn: overrides.engineOn,
    rateLh: overrides.rateLh,
    authoritative: overrides.authoritative === true,
  };
};

/** Normalize one raw/smoothed fuel-signal row returned by the batch API. */
export const normalizeFuelSignalSample = (row = {}, options = {}) => {
  const { fuelSource, tankCapacityL = 0 } = options;

  const rawFuelL = resolveRowFuelLitres(row, fuelSource, tankCapacityL);
  const confirmedFuelL = pickNumeric(
    row.confirmedFuelL,
    row.confirmed_fuel_l,
    rawFuelL,
  );

  const { latitude, longitude } = resolveRowLocation(row);

  return {
    timestamp: resolveRowTimestamp(row),
    rawFuelL,
    confirmedFuelL,
    fuelLevelLitre: rawFuelL,
    latitude,
    longitude,
    estimatedCostRM: pickNumeric(
      row.estimatedCostRM,
      row.estimated_cost_rm,
      row.estCostRm,
      row.est_cost_rm,
      row.estRm,
      row.est_rm,
    ),
    motionInference: pickNumeric(row.motionInference, row.motion_inference),
  };
};

/** Same refill/drain episode can emit multiple backend transitions — merge like Fuel Ledger. */
const REFILL_DRAIN_MERGE_GAP_MS = 30 * 60 * 1000;

const isActionableFuelEvent = (eventFilterKey) => (
  eventFilterKey === FUEL_EVENT_FILTER.REFILL
  || eventFilterKey === FUEL_EVENT_FILTER.DRAIN
);

const resolveRunWindowEnd = (endX, eventFilterKey) => {
  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL || eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
    // Debounced level may settle after the backend transition timestamp.
    return endX + REFILL_DRAIN_MERGE_GAP_MS;
  }
  return endX + 60 * 1000;
};

/** Final settled tank level at the close of a refill/drain run (Fuel Ledger: smooth[endIdx]). */
const resolveLevelAfterFromSeries = (confirmedSeries, startX, endX, eventFilterKey) => {
  if (!confirmedSeries.length || endX == null) return null;

  const windowStart = (startX ?? endX) - 60 * 1000;
  const windowEnd = resolveRunWindowEnd(endX, eventFilterKey);
  const points = confirmedSeries.filter((point) => point.x >= windowStart && point.x <= windowEnd);
  if (!points.length) return null;

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
    return Number(Math.max(...points.map((point) => point.y)).toFixed(1));
  }

  if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
    return Number(Math.min(...points.map((point) => point.y)).toFixed(1));
  }

  const lastPoint = points[points.length - 1];
  return lastPoint?.y ?? null;
};

const pickEventLocation = (group) => {
  for (let index = group.length - 1; index >= 0; index -= 1) {
    const { latitude, longitude } = group[index];
    if (latitude != null && longitude != null) return { latitude, longitude };
  }
  return { latitude: null, longitude: null };
};

const sampleFuelLevel = (sample) => sample.rawFuelL;

const isStrictStationaryMotion = (motionInference) => {
  const code = Number(motionInference);
  return code === 100 || code === 400 || code === 450 || code === 460;
};

const isLooseStationaryMotion = (motionInference) => {
  if (motionInference == null) return false;
  const code = Number(motionInference);
  if (code === 200 || code === 300) return false;
  return isStrictStationaryMotion(code) || code === 150;
};

const findEntryIndexNear = (entries, targetX) => {
  if (!entries.length || targetX == null) return 0;
  let bestIdx = 0;
  let bestDist = Infinity;
  entries.forEach((entry, index) => {
    const dist = Math.abs(entry.x - targetX);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = index;
    }
  });
  return bestIdx;
};

const findRunEndTimestamp = (confirmedSeries, startX, fallbackEndX, eventFilterKey) => {
  const windowStart = (startX ?? fallbackEndX) - 60 * 1000;
  const windowEnd = (fallbackEndX ?? startX) + REFILL_DRAIN_MERGE_GAP_MS;
  const points = confirmedSeries.filter(
    (point) => point.x >= windowStart && point.x <= windowEnd,
  );
  if (!points.length) return fallbackEndX ?? startX;

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
    let peakLevel = -Infinity;
    let peakX = fallbackEndX ?? startX;
    points.forEach((point) => {
      if (point.y > peakLevel) {
        peakLevel = point.y;
        peakX = point.x;
      }
    });
    return peakX;
  }

  if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
    let troughLevel = Infinity;
    let troughX = fallbackEndX ?? startX;
    points.forEach((point) => {
      if (point.y < troughLevel) {
        troughLevel = point.y;
        troughX = point.x;
      }
    });
    return troughX;
  }

  return fallbackEndX ?? startX;
};

const buildSampleEntries = (samples, windowStart, windowEnd) => (
  samples
    .map((sample) => ({
      sample,
      x: toTimestamp(sample.timestamp),
    }))
    .filter((entry) => entry.x != null && entry.x >= windowStart && entry.x <= windowEnd)
);

const estimateGraceSamples = (entries, onsetIdx, endIdx) => {
  const slice = entries.slice(onsetIdx, Math.min(endIdx + 1, entries.length));
  const gaps = [];
  for (let index = 1; index < slice.length; index += 1) {
    gaps.push(slice[index].x - slice[index - 1].x);
  }
  gaps.sort((a, b) => a - b);
  const medianCadenceMs = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 60000;
  return Math.max(6, Math.ceil(120 * 1000 / Math.max(1, medianCadenceMs)));
};

const findRawRiseOnsetIndex = (entries, endIdx) => {
  if (!entries.length) return 0;
  const i0 = Math.max(0, endIdx - 20);
  const lo = Math.max(0, i0 - 4);
  const baseLevels = entries
    .slice(lo, i0 + 1)
    .map((entry) => sampleFuelLevel(entry.sample))
    .filter((value) => value != null)
    .sort((a, b) => a - b);
  if (!baseLevels.length) return i0;

  const base = baseLevels[Math.floor(baseLevels.length / 2)];
  const step = 1.35;

  for (let index = Math.max(0, i0); index <= endIdx; index += 1) {
    const fuel = sampleFuelLevel(entries[index].sample);
    if (fuel == null || fuel < base + step) continue;
    const confirmIndex = Math.min(endIdx, index + 2);
    const confirmFuel = sampleFuelLevel(entries[confirmIndex].sample);
    if (confirmFuel != null && confirmFuel >= base + step) return index;
  }

  return i0;
};

const findDrainPeakIndex = (entries, endIdx) => {
  if (!entries.length) return 0;
  let peakIdx = 0;
  let peakLevel = -Infinity;
  entries.slice(0, endIdx + 1).forEach((entry, index) => {
    const fuel = sampleFuelLevel(entry.sample);
    if (fuel != null && fuel > peakLevel) {
      peakLevel = fuel;
      peakIdx = index;
    }
  });
  return peakIdx;
};

const REFILL_RUN_LOOKBACK_MS = 30 * 60 * 1000;
const DEFAULT_SLOSH_DURATION_S = 120;

const estimateMedianCadenceMs = (series) => {
  const gaps = [];
  for (let index = 1; index < Math.min(series.length, 500); index += 1) {
    gaps.push(series[index].x - series[index - 1].x);
  }
  gaps.sort((a, b) => a - b);
  return gaps.length ? Math.max(1, gaps[Math.floor(gaps.length / 2)]) : 60000;
};

const medianSmoothSeries = (series, sloshDurationS = DEFAULT_SLOSH_DURATION_S) => {
  if (!series.length) return [];
  const cadenceMs = estimateMedianCadenceMs(series);
  let windowSize = Math.max(Math.ceil((sloshDurationS * 1000) / cadenceMs), 5);
  if (windowSize % 2 === 0) windowSize += 1;
  if (windowSize > 41) windowSize = 41;
  const half = Math.floor(windowSize / 2);

  return series.map((point, index) => {
    const lo = Math.max(0, index - half);
    const hi = Math.min(series.length - 1, index + half);
    const segment = [];
    for (let cursor = lo; cursor <= hi; cursor += 1) {
      segment.push(series[cursor].y);
    }
    segment.sort((a, b) => a - b);
    return { x: point.x, y: segment[Math.floor(segment.length / 2)] };
  });
};

const computeMonotonicRunSum = (smoothSeries, startIdx, endIdx, eventFilterKey) => {
  if (!smoothSeries.length || startIdx == null || endIdx == null) return null;
  const lo = Math.max(0, Math.min(startIdx, endIdx));
  const hi = Math.max(startIdx, endIdx);
  if (hi - lo < 1) return null;

  let runSum = 0;
  let runSign = 0;
  for (let index = lo + 1; index <= hi; index += 1) {
    const delta = smoothSeries[index].y - smoothSeries[index - 1].y;
    if (delta === 0) continue;
    const sign = Math.sign(delta);
    if (runSign === 0) {
      runSign = sign;
      runSum = delta;
      continue;
    }
    if (sign === runSign || Math.abs(delta) < 0.5) {
      runSum += delta;
      continue;
    }
    break;
  }

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL && runSign > 0 && runSum >= 0.5) {
    return Number(runSum.toFixed(1));
  }
  if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN && runSign < 0 && runSum <= -0.5) {
    return Number(runSum.toFixed(1));
  }
  return null;
};

const resolveRefuelRunContext = (samples, confirmedSeries, startX, endX, eventFilterKey) => {
  const runEndX = findRunEndTimestamp(confirmedSeries, startX, endX, eventFilterKey);
  const runStart = Math.min(startX, runEndX) - REFILL_RUN_LOOKBACK_MS;
  const runEnd = Math.max(startX, runEndX) + 5 * 60 * 1000;
  const entries = buildSampleEntries(samples, runStart, runEnd);
  const runEndIdx = entries.length ? findEntryIndexNear(entries, runEndX) : 0;
  let onsetIdx = entries.length ? findEntryIndexNear(entries, startX) : 0;

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
    onsetIdx = findRawRiseOnsetIndex(entries, runEndIdx);
  } else if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
    onsetIdx = findDrainPeakIndex(entries, runEndIdx);
  }

  return {
    entries,
    onsetIdx,
    runEndIdx,
    onsetX: entries[onsetIdx]?.x ?? startX,
    runEndX,
  };
};

const resolvePreEventBaselineLevel = (entries, onsetIdx) => {
  if (!entries.length) return null;
  const lo = Math.max(0, onsetIdx - 4);
  const levels = entries
    .slice(lo, onsetIdx + 1)
    .map((entry) => sampleFuelLevel(entry.sample))
    .filter((value) => value != null)
    .sort((a, b) => a - b);
  if (!levels.length) return null;
  return levels[Math.floor(levels.length / 2)];
};

const computeRunDeltaFromSeries = (
  confirmedSeries,
  rawSeries,
  samples,
  startX,
  endX,
  eventFilterKey,
) => {
  if (startX == null || endX == null) return null;

  const {
    entries, onsetIdx, onsetX, runEndX,
  } = resolveRefuelRunContext(samples, confirmedSeries, startX, endX, eventFilterKey);

  if (rawSeries.length >= 2) {
    const rawWindow = rawSeries.filter(
      (point) => point.x >= (onsetX - 60 * 1000) && point.x <= runEndX + 60 * 1000,
    );
    if (rawWindow.length >= 2) {
      const smoothed = medianSmoothSeries(rawWindow);
      const smoothOnsetIdx = findEntryIndexNear(smoothed, onsetX);
      const smoothEndIdx = findEntryIndexNear(smoothed, runEndX);
      const rawRunSum = computeMonotonicRunSum(
        smoothed,
        smoothOnsetIdx,
        smoothEndIdx,
        eventFilterKey,
      );
      if (rawRunSum != null) return rawRunSum;
    }
  }

  if (rawSeries.length >= 2) {
    const rawWindow = rawSeries.filter(
      (point) => point.x >= (entries[0]?.x ?? onsetX - REFILL_RUN_LOOKBACK_MS)
        && point.x <= runEndX + 60 * 1000,
    );
    if (rawWindow.length >= 2) {
      const smoothed = medianSmoothSeries(rawWindow);
      const baseline = resolvePreEventBaselineLevel(entries, onsetIdx)
        ?? smoothed[findEntryIndexNear(smoothed, onsetX)]?.y;
      const settledPoints = smoothed.filter((point) => point.x <= runEndX + 60 * 1000);
      if (baseline != null && settledPoints.length) {
        if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
          const peak = Math.max(...settledPoints.map((point) => point.y));
          const rise = Number((peak - baseline).toFixed(1));
          if (rise >= 0.5) return rise;
        }
        if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
          const trough = Math.min(...settledPoints.map((point) => point.y));
          const drop = Number((trough - baseline).toFixed(1));
          if (drop <= -0.5) return drop;
        }
      }
    }
  }

  if (confirmedSeries.length >= 2) {
    const confirmedWindow = confirmedSeries.filter(
      (point) => point.x >= onsetX && point.x <= runEndX + 60 * 1000,
    );
    if (confirmedWindow.length >= 2) {
      const confirmedRunSum = computeMonotonicRunSum(
        confirmedWindow,
        0,
        confirmedWindow.length - 1,
        eventFilterKey,
      );
      if (confirmedRunSum != null) return confirmedRunSum;
    }
  }

  const windowStart = (entries[0]?.x ?? onsetX) - 60 * 1000;
  const windowEnd = resolveRunWindowEnd(runEndX, eventFilterKey);
  const points = confirmedSeries.filter(
    (point) => point.x >= windowStart && point.x <= windowEnd,
  );
  if (points.length >= 2) {
    const levels = points.map((point) => point.y);
    const minLevel = Math.min(...levels);
    const maxLevel = Math.max(...levels);
    if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
      const rise = Number((maxLevel - minLevel).toFixed(1));
      if (rise >= 0.5) return rise;
    }
    if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
      const drop = Number((minLevel - maxLevel).toFixed(1));
      if (drop <= -0.5) return drop;
    }
  }

  const baseline = resolvePreEventBaselineLevel(entries, onsetIdx);
  const levelAfter = resolveLevelAfterFromSeries(confirmedSeries, startX, endX, eventFilterKey);
  if (baseline != null && levelAfter != null) {
    const delta = Number((levelAfter - baseline).toFixed(1));
    if (eventFilterKey === FUEL_EVENT_FILTER.REFILL && delta >= 0.5) return delta;
    if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN && delta <= -0.5) return delta;
  }

  return null;
};

const pickStationaryIndexFromLedger = (entries, onsetIdx, runEndIdx) => {
  if (!entries.length) return -1;

  const graceSamples = estimateGraceSamples(entries, onsetIdx, runEndIdx);
  const hi = Math.min(entries.length - 1, runEndIdx + graceSamples);
  const lo = Math.max(0, onsetIdx - 3);
  let loosePick = -1;

  for (let index = onsetIdx; index <= hi; index += 1) {
    const { sample } = entries[index];
    if (sample.latitude == null || sample.longitude == null) continue;
    if (isStrictStationaryMotion(sample.motionInference)) return index;
    if (loosePick < 0 && isLooseStationaryMotion(sample.motionInference)) loosePick = index;
  }

  for (let index = onsetIdx - 1; index >= lo; index -= 1) {
    const { sample } = entries[index];
    if (sample.latitude == null || sample.longitude == null) continue;
    if (isStrictStationaryMotion(sample.motionInference)) return index;
  }

  return loosePick;
};

const pickStationaryLocationFrom = (entries, onsetIdx, runEndIdx) => {
  const stationaryIdx = pickStationaryIndexFromLedger(entries, onsetIdx, runEndIdx);
  if (stationaryIdx >= 0) {
    const { latitude, longitude } = entries[stationaryIdx].sample;
    return { latitude, longitude };
  }

  const onsetSample = entries[onsetIdx]?.sample;
  if (onsetSample?.latitude != null && onsetSample?.longitude != null) {
    return { latitude: onsetSample.latitude, longitude: onsetSample.longitude };
  }

  return { latitude: null, longitude: null };
};

/**
 * Fuel Ledger: GPS at the stationary nozzle-in moment (onset if stopped, else first
 * stopped sample after fuel starts rising), not the backend transition timestamp.
 */
const resolveStationaryEventLocation = (samples, confirmedSeries, startX, endX, eventFilterKey) => {
  if (!samples.length || startX == null) return { latitude: null, longitude: null };

  const runEndX = findRunEndTimestamp(confirmedSeries, startX, endX, eventFilterKey);
  const runStart = Math.min(startX, runEndX) - 5 * 60 * 1000;
  const runEnd = Math.max(startX, runEndX) + 5 * 60 * 1000;
  const entries = buildSampleEntries(samples, runStart, runEnd);
  if (!entries.length) return { latitude: null, longitude: null };

  const runEndIdx = findEntryIndexNear(entries, runEndX);
  let onsetIdx = findEntryIndexNear(entries, startX);

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
    onsetIdx = findRawRiseOnsetIndex(entries, runEndIdx);
  } else if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
    onsetIdx = findDrainPeakIndex(entries, runEndIdx);
  }

  const stationary = pickStationaryLocationFrom(entries, onsetIdx, runEndIdx);
  if (stationary.latitude != null && stationary.longitude != null) {
    return {
      ...stationary,
      timeStart: entries[onsetIdx]?.sample?.timestamp ?? null,
      xStart: entries[onsetIdx]?.x ?? startX,
    };
  }

  const onsetSample = entries[onsetIdx]?.sample;
  if (onsetSample?.latitude != null && onsetSample?.longitude != null) {
    return {
      latitude: onsetSample.latitude,
      longitude: onsetSample.longitude,
      timeStart: onsetSample.timestamp,
      xStart: entries[onsetIdx]?.x ?? startX,
    };
  }

  return { latitude: null, longitude: null, timeStart: null, xStart: startX };
};

const resolveEventLocation = (group, samples, confirmedSeries, startX, endX, eventFilterKey) => {
  if (isActionableFuelEvent(eventFilterKey)) {
    const stationary = resolveStationaryEventLocation(
      samples,
      confirmedSeries,
      startX,
      endX,
      eventFilterKey,
    );
    if (stationary.latitude != null && stationary.longitude != null) return stationary;
  }

  return { ...pickEventLocation(group), timeStart: null, xStart: null };
};

const mergeRefillDrainEventGroup = (group, confirmedSeries, samples, rawSeries) => {
  if (!group.length) return null;

  const eventFilterKey = group[0].eventFilterKey;
  const first = group[0];
  const last = group[group.length - 1];
  const startX = first.xStart ?? first.x;
  const endX = last.x;
  const runContext = resolveRefuelRunContext(
    samples,
    confirmedSeries,
    startX,
    endX,
    eventFilterKey,
  );

  const authoritativeDeltas = group
    .map((event) => event.deltaLitres)
    .filter((delta) => delta != null);
  let deltaLitres = authoritativeDeltas.length
    ? Number(authoritativeDeltas.reduce((sum, delta) => sum + delta, 0).toFixed(1))
    : computeRunDeltaFromSeries(
      confirmedSeries,
      rawSeries,
      samples,
      startX,
      endX,
      eventFilterKey,
    );
  if (deltaLitres == null) {
    const levelAfter = last.levelAfter ?? last.fuelLevelLitre;
    const levelBefore = resolvePreEventBaselineLevel(runContext.entries, runContext.onsetIdx)
      ?? first.baselineLevel
      ?? first.fuelLevelLitre
      ?? first.levelAfter;
    if (levelAfter != null && levelBefore != null) {
      deltaLitres = Number((levelAfter - levelBefore).toFixed(1));
    }
  }

  if (eventFilterKey === FUEL_EVENT_FILTER.REFILL && deltaLitres != null && deltaLitres < 0) {
    deltaLitres = Math.abs(deltaLitres);
  }
  if (eventFilterKey === FUEL_EVENT_FILTER.DRAIN && deltaLitres != null && deltaLitres > 0) {
    deltaLitres = -Math.abs(deltaLitres);
  }

  const location = resolveEventLocation(group, samples, confirmedSeries, startX, endX, eventFilterKey);
  const levelAfter = resolveLevelAfterFromSeries(confirmedSeries, startX, endX, eventFilterKey)
    ?? last.levelAfter
    ?? last.fuelLevelLitre;

  return {
    ...last,
    x: endX,
    time: last.time,
    timeStart: location.timeStart ?? first.timeStart ?? first.time,
    xStart: location.xStart ?? startX,
    deltaLitres,
    levelAfter,
    latitude: location.latitude ?? last.latitude,
    longitude: location.longitude ?? last.longitude,
    estimatedCostRM: null,
  };
};

const mergeAdjacentRefillDrainEvents = (events, confirmedSeries, samples, rawSeries) => {
  // The batch ledger has already consolidated the complete raw signal before the API
  // downsamples chart samples. Re-deriving an authoritative event from those chart
  // samples shifts its onset, GPS fix and settled level, and rounds its exact delta.
  if (events.length > 0 && events.every((event) => event.authoritative)) {
    return events.slice().sort((a, b) => b.x - a.x);
  }

  const diagnostics = events.filter(
    (event) => !isActionableFuelEvent(event.eventFilterKey),
  );
  const actionable = events
    .filter((event) => isActionableFuelEvent(event.eventFilterKey))
    .sort((a, b) => a.x - b.x);

  const consolidated = [];
  let group = [];

  const flushGroup = () => {
    const merged = mergeRefillDrainEventGroup(group, confirmedSeries, samples, rawSeries);
    if (merged) consolidated.push(merged);
    group = [];
  };

  actionable.forEach((event) => {
    if (group.length === 0) {
      group.push(event);
      return;
    }

    const sameType = event.eventFilterKey === group[0].eventFilterKey;
    const gapMs = event.x - group[group.length - 1].x;
    const groupStart = group[0].xStart;
    const hasEpisodeStart = event.xStart != null && groupStart != null;
    const sameEpisode = hasEpisodeStart
      ? event.xStart === groupStart
      : gapMs <= REFILL_DRAIN_MERGE_GAP_MS;
    if (sameType && sameEpisode) {
      group.push(event);
      return;
    }

    flushGroup();
    group = [event];
  });

  flushGroup();

  return [...consolidated, ...diagnostics].sort((a, b) => b.x - a.x);
};

const buildMarkerFromEvent = (event) => {
  const y = event.levelAfter ?? event.fuelLevelLitre;
  if (event.x == null || y == null) return null;
  return { x: event.x, y };
};

const motionBandClass = (motionInference) => {
  switch (Number(motionInference)) {
    case 200:
    case 150:
      return 'moving';
    case 400:
    case 450:
    case 460:
      return 'idle';
    default:
      return 'stopped';
  }
};

const motionBandLabel = (motionInference) => {
  switch (Number(motionInference)) {
    case 200:
      return 'Moving';
    case 150:
      return 'Movement detected';
    case 400:
    case 450:
    case 460:
      return 'Idle';
    case 300:
      return 'Towing';
    default:
      return 'Stopped';
  }
};

/**
 * Build chart series and event summaries from batch-calculated samples/events.
 */
export const buildFuelLevelSignalView = (rows = [], options = {}) => {
  const samples = (rows || [])
    .map((row) => normalizeFuelSignalSample(row, options))
    .filter((sample) => sample.timestamp && sample.rawFuelL != null)
    .sort((a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf());

  const rawSeries = [];
  const confirmedSeries = [];
  let carriedConfirmed = null;

  samples.forEach((sample) => {
    const x = toTimestamp(sample.timestamp);
    if (x == null) return;

    const rawY = sample.rawFuelL;
    if (rawY == null) return;

    rawSeries.push({ x, y: rawY });

    const confirmedY = sample.confirmedFuelL ?? carriedConfirmed;
    if (confirmedY != null) {
      carriedConfirmed = confirmedY;
      confirmedSeries.push({ x, y: confirmedY });
    }
  });

  const motionSegments = [];
  if (samples.length > 0) {
    let segmentStart = toTimestamp(samples[0].timestamp);
    let segmentMotion = samples[0].motionInference;
    let segmentEnd = segmentStart;

    samples.forEach((sample, index) => {
      const x = toTimestamp(sample.timestamp);
      if (x == null) return;

      if (index === 0) {
        segmentEnd = x;
        return;
      }

      if (sample.motionInference !== segmentMotion) {
        motionSegments.push({
          start: segmentStart,
          end: segmentEnd,
          motionInference: segmentMotion,
          bandClass: motionBandClass(segmentMotion),
          label: motionBandLabel(segmentMotion),
        });
        segmentStart = x;
        segmentMotion = sample.motionInference;
      }
      segmentEnd = x;
    });

    motionSegments.push({
      start: segmentStart,
      end: segmentEnd,
      motionInference: segmentMotion,
      bandClass: motionBandClass(segmentMotion),
      label: motionBandLabel(segmentMotion),
    });
  }

  const rangeStart = samples.length > 0 ? toTimestamp(samples[0].timestamp) : null;
  const rangeEnd = samples.length > 0 ? toTimestamp(samples[samples.length - 1].timestamp) : null;

  const apiEvents = (options.apiEvents || [])
    .map((row) => {
      const sample = normalizeFuelSignalSample(row, options);
      const eventLabel = row.event
        || row.eventType
        || row.event_type
        || 'ALERT';
      const normalizedLabel = String(eventLabel).toUpperCase().includes('REFILL') && !String(eventLabel).includes('REJECTED')
        ? 'REFILL'
        : eventLabel;
      const eventTs = toTimestamp(
        row.timestamp
        || row.ts
        || row.time
        || sample.timestamp,
      );
      const eventTsStart = toTimestamp(
        row.tsStart
        || row.ts_start
        || row.timeStart
        || row.time_start,
      );
      return buildEventRecord(sample, {
        x: eventTs,
        xStart: eventTsStart ?? eventTs,
        event: normalizedLabel,
        eventFilterKey: eventLabelToFilterKey(normalizedLabel),
        deltaLitres: pickNumeric(row.deltaLitres, row.delta_litres, row.deltaL),
        levelAfter: pickNumeric(row.levelAfter, row.level_after, sample.confirmedFuelL),
        fuelLevelLitre: pickNumeric(row.fuelLevelLitre, row.fuel_level_litre, sample.fuelLevelLitre),
        baselineLevel: pickNumeric(row.baselineLevel, row.baseline_level),
        timeStart: eventTsStart != null ? (row.tsStart || row.ts_start || row.timeStart || row.time_start) : undefined,
        eventSub: row.sub ?? row.eventSub ?? row.event_sub,
        engineOn: row.engineOn ?? row.engine_on,
        rateLh: pickNumeric(row.rateLh, row.rate_lh),
        authoritative: true,
      });
    })
    .filter((event) => event.x != null)
    .map((event) => ({
      ...event,
      timeStart: event.timeStart ?? event.time,
      xStart: event.xStart ?? event.x,
    }));

  const displayEvents = mergeAdjacentRefillDrainEvents(
    apiEvents,
    confirmedSeries,
    samples,
    rawSeries,
  );

  const consolidatedRefills = displayEvents.filter(
    (event) => event.eventFilterKey === FUEL_EVENT_FILTER.REFILL,
  );
  const consolidatedDrains = displayEvents.filter(
    (event) => event.eventFilterKey === FUEL_EVENT_FILTER.DRAIN,
  );
  const spikeRejected = displayEvents.filter(
    (event) => event.eventFilterKey === FUEL_EVENT_FILTER.SPIKE_REJECTED,
  ).length;
  const reportedCounts = options.sampleCounts && typeof options.sampleCounts === 'object'
    ? options.sampleCounts
    : null;
  const rawInputSamples = pickNumeric(reportedCounts?.rawInputSamples)
    ?? samples.length;
  const fuelBearingSamples = pickNumeric(reportedCounts?.fuelBearingSamples)
    ?? samples.length + spikeRejected;
  const cleanSamples = pickNumeric(reportedCounts?.cleanSamples)
    ?? samples.length;
  const reportedSpikeRejected = pickNumeric(reportedCounts?.spikeRejected)
    ?? spikeRejected;

  return {
    rawSeries,
    confirmedSeries,
    events: displayEvents,
    refillMarkers: consolidatedRefills.map(buildMarkerFromEvent).filter(Boolean),
    drainMarkers: consolidatedDrains.map(buildMarkerFromEvent).filter(Boolean),
    motionSegments,
    rangeStart,
    rangeEnd,
    signalChain: {
      inputSamples: rawInputSamples,
      rawInputSamples,
      fuelBearingSamples,
      cleanSamples,
      returnedSamples: pickNumeric(reportedCounts?.returnedSamples) ?? samples.length,
      hasDetailedCounts: reportedCounts !== null,
      spikeRejected: reportedSpikeRejected,
      refillsConfirmed: consolidatedRefills.length,
      drainsConfirmed: consolidatedDrains.length,
    },
  };
};

export const extractFuelSignalRows = (payload) => {
  const parsed = parseJsonIfString(payload);
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed.data)) return parsed.data;
  if (Array.isArray(parsed.samples)) return parsed.samples;
  if (Array.isArray(parsed.rows)) return parsed.rows;
  if (Array.isArray(parsed.telemetry)) return parsed.telemetry;
  if (Array.isArray(parsed.records)) return parsed.records;
  if (Array.isArray(parsed.results)) return parsed.results;
  return [];
};

const normalizeSignalSampleCounts = (root, nested) => {
  const source = root?.sampleCounts
    ?? root?.sample_counts
    ?? nested?.sampleCounts
    ?? nested?.sample_counts;
  if (!source || typeof source !== 'object') return null;
  return {
    rawInputSamples: pickNumeric(source.rawInputSamples, source.raw_input_samples),
    fuelBearingSamples: pickNumeric(source.fuelBearingSamples, source.fuel_bearing_samples),
    spikeRejected: pickNumeric(source.spikeRejected, source.spike_rejected),
    cleanSamples: pickNumeric(source.cleanSamples, source.clean_samples),
    returnedSamples: pickNumeric(source.returnedSamples, source.returned_samples),
  };
};

/** Parse API wrapper and return rows + metadata from getFuelLevelSignal / telemetryLogReport. */
export const resolveFuelSignalApiPayload = (result) => {
  const httpStatus = result?.status ?? result?.response?.status ?? null;
  const responseBody = result?.data ?? result?.response?.data;
  const root = parseJsonIfString(responseBody ?? result);

  // HTTP 204 No Content — valid success with no body (no samples for this query).
  if (httpStatus === 204) {
    return {
      rows: [],
      apiEvents: [],
      sampleCounts: null,
      totalData: 0,
      status: 204,
      httpStatus: 204,
      isNoContent: true,
    };
  }

  if (!root || typeof root !== 'object') {
    return {
      rows: [],
      apiEvents: [],
      sampleCounts: null,
      totalData: 0,
      status: httpStatus,
      httpStatus,
      isNoContent: false,
    };
  }

  const nested = parseJsonIfString(root.data ?? root.result ?? root.payload);
  const rows = extractFuelSignalRows(nested ?? root);
  const apiEvents = extractFuelSignalRows(
    root.events
    ?? root.eventLog
    ?? root.event_log
    ?? nested?.events
    ?? nested?.eventLog,
  );
  const totalData = pickNumeric(root.totalData, root.total_data, nested?.totalData, nested?.total_data)
    ?? rows.length;
  const sampleCounts = normalizeSignalSampleCounts(root, nested);
  const appStatus = pickNumeric(root.status, root.code);

  return {
    rows,
    apiEvents,
    sampleCounts,
    totalData,
    status: appStatus,
    httpStatus,
    isNoContent: appStatus === 204 || (rows.length === 0 && appStatus === 204),
  };
};

export const FLEET_EVENT_TIMELINE_COLORS = {
  REFILL: '#00868B',
  DRAIN: '#B42318',
  ALERT: '#B54708',
};

const TIMELINE_EVENT_FILTER_KEYS = new Set([
  FUEL_EVENT_FILTER.REFILL,
  FUEL_EVENT_FILTER.DRAIN,
  FUEL_EVENT_FILTER.ALERT,
]);

/**
 * Build scatter-series data for the fleet refill/drain/alert timeline.
 * One horizontal lane per asset; points are { x, y, asset, ...eventMeta }.
 */
export const buildFleetEventTimelineView = (
  assetEventSets = [],
  allAssets = [],
  options = {},
) => {
  const assets = [...new Set(
    (allAssets.length > 0 ? allAssets : assetEventSets.map((entry) => entry.asset))
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b));

  const assetIndexByName = new Map(assets.map((name, index) => [name, index]));
  const refillPoints = [];
  const drainPoints = [];
  const alertPoints = [];
  let totalRefills = 0;
  let totalDrains = 0;
  let totalAlerts = 0;

  assetEventSets.forEach(({ asset, deviceOid, events = [] }) => {
    const lane = assetIndexByName.get(asset);
    if (lane == null) return;

    events.forEach((event) => {
      if (!TIMELINE_EVENT_FILTER_KEYS.has(event.eventFilterKey) || event.x == null) return;

      const point = {
        x: event.x,
        y: lane,
        asset,
        deviceOid,
        event: event.event,
        eventFilterKey: event.eventFilterKey,
        deltaLitres: event.deltaLitres,
        levelAfter: event.levelAfter,
        time: event.time,
        latitude: event.latitude,
        longitude: event.longitude,
        motionLabel: event.motionLabel,
      };

      if (event.eventFilterKey === FUEL_EVENT_FILTER.REFILL) {
        refillPoints.push(point);
        totalRefills += 1;
      } else if (event.eventFilterKey === FUEL_EVENT_FILTER.DRAIN) {
        drainPoints.push(point);
        totalDrains += 1;
      } else {
        alertPoints.push(point);
        totalAlerts += 1;
      }
    });
  });

  const series = [
    { name: 'REFILL', data: refillPoints, color: FLEET_EVENT_TIMELINE_COLORS.REFILL },
    { name: 'DRAIN', data: drainPoints, color: FLEET_EVENT_TIMELINE_COLORS.DRAIN },
    { name: 'ALERT', data: alertPoints, color: FLEET_EVENT_TIMELINE_COLORS.ALERT },
  ].filter((entry) => entry.data.length > 0);

  return {
    assets,
    assetIndexByName,
    series,
    totals: {
      refills: totalRefills,
      drains: totalDrains,
      alerts: totalAlerts,
    },
    hasEvents: series.length > 0,
    filterRangeStart: options.filterRangeStart ?? null,
    filterRangeEnd: options.filterRangeEnd ?? null,
  };
};
